import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const paycor = defineConnector({
  identifier: "paycor",
  name: "Paycor",
  tagLine: "Payroll and human capital management",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: () => "https://apis\\.paycor\\.com/.*",
  auth: (app) =>
    headerAuth([
      {
        name: "ocp-apim-subscription-key",
        value: app.config.subscriptionKey,
      },
    ]),
  steps: [
    {
      title: "Enter your Paycor instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Paycor instance.",
        },
        {
          kind: "input",
          label: "Subscription Key",
          placeholder: "your-subscriptionKey",
          required: true,
          target: { kind: "configuration", field: "subscriptionKey" },
          helperText: "The subscription key for your Paycor account",
        },
        {
          kind: "input",
          label: "Company ID",
          placeholder: "290180",
          required: true,
          target: { kind: "configuration", field: "tenantId" },
          helperText: "The company ID for your Paycor account",
        },
      ],
    },
    {
      title: "Create a Paycor app",
      items: [
        {
          kind: "markdown",
          content:
            "Open the [Paycor developer getting started guide](https://developer.paycor.com/get-started) and create an OAuth application in the Paycor developer portal. Copy the client ID and client secret from the app you create.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/paycor/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Configure scopes",
      items: [
        {
          kind: "input",
          label: "Scopes",
          placeholder: "offline_access",
          helperText: "Space-separated list of permission scopes.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Paycor account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Paycor account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () =>
      "https://hcm.paycor.com/AppActivation/Authorize",
    tokenEndpoint: (configuration) =>
      `https://apis.paycor.com/sts/v1/common/token?subscription-key=${configuration.subscriptionKey}`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: () => "https://apis.paycor.com/",
  },
  publicEnvVars: ({ tenantId }) => ({
    PAYCOR_TENANT_ID: tenantId || "",
  }),
});
